/**
 * useAbortController — stable abort signal lifecycle for effects.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import { useCallback, useEffect, useRef, useState } from "react";

export interface UseAbortControllerResult {
  readonly signal: AbortSignal;
  readonly abort: () => void;
  readonly reset: () => AbortSignal;
}

function createController(): AbortController {
  return new AbortController();
}

export function useAbortController(): UseAbortControllerResult {
  const [controller, setController] = useState<AbortController>(createController);
  const controllerRef = useRef<AbortController>(controller);
  controllerRef.current = controller;

  const abort = useCallback((): void => {
    controllerRef.current.abort();
  }, []);

  const reset = useCallback((): AbortSignal => {
    controllerRef.current.abort();
    const next = createController();
    controllerRef.current = next;
    setController(next);
    return controllerRef.current.signal;
  }, []);

  useEffect(
    () => (): void => {
      controllerRef.current.abort();
    },
    [],
  );

  return {
    signal: controller.signal,
    abort,
    reset,
  };
}
